/* ═══════════════════════════════════════════════════════════════════════════
   The entry. Reads the URL, picks the surface, and gets out of the way.

   Every surface below is lazy: a customer booking a haircut should not
   download the platform admin. They share the seam in api/, the rules in
   domain/, the vocabulary in industries/ and the components — and nothing
   else. That is the whole architecture.
   ═══════════════════════════════════════════════════════════════════════ */
import { useState, useEffect, lazy, Suspense } from 'react';
import { route } from './router.js';
import { ToastHost } from './components/Toast.jsx';
import Surfaces from './components/Surfaces.jsx';
import Boundary from './components/Boundary.jsx';

/* The tab title. Without this every surface inherits the one in index.html,
   and four windows open side by side all claim to be the same page. The tail
   websites set their own and are left alone. */
const TITLES = {
  customer: 'Avtale.net · Find a business',
  business: 'Avtale.net · Business',
  staff:    'Avtale.net · Staff',
  admin:    'Avtale.net · Platform',
};

const Customer = lazy(() => import('./surfaces/customer/App.jsx'));
const Website  = lazy(() => import('./surfaces/website/Site.jsx'));
/* The card on a table. Its own screen rather than a mode of the website:
   somebody standing at table 7 with a telephone wants the menu and the bill,
   not a marketing page with a booking button on it. */
const Table    = lazy(() => import('./surfaces/website/Table.jsx'));
const Business = lazy(() => import('./surfaces/business/App.jsx'));
const Staff    = lazy(() => import('./surfaces/staff/App.jsx'));
const Admin    = lazy(() => import('./surfaces/admin/App.jsx'));

export default function Root() {
  const [where, setWhere] = useState(route);

  useEffect(() => {
    const onPop = () => setWhere(route());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    if (TITLES[where.surface]) document.title = TITLES[where.surface];
  }, [where.surface]);

  return (
    <ToastHost>
      {/* One screen throwing must not take the page to white with nothing on
          it. The boundary is keyed on the surface so that moving to another
          one clears the error rather than leaving the person stuck on the
          broken screen — which, without the key, is exactly what happens. */}
      <Boundary key={where.surface + (where.slug || '')}>
      <Suspense fallback={<div className="loading">Loading…</div>}>
        {where.surface === 'website' && where.table
          ? <Table slug={where.slug} table={where.table} />
          : where.surface === 'website' && <Website slug={where.slug} />}
        {where.surface === 'business' && <Business />}
        {where.surface === 'staff'    && <Staff />}
        {where.surface === 'admin'    && <Admin />}
        {where.surface === 'customer' && <Customer />}
      </Suspense>
      </Boundary>
      <Surfaces here={where.surface} />
    </ToastHost>
  );
}
